import { DEFAULT_LOCATION_COORDS } from 'config/defaultLocation.config.js';
import useLocation from 'hooks/useLocation';
import { useMemo } from 'react';
import { useGetFusionPlacesQuery } from 'services/user/places/places.api';
import { DOPPLER_CONFIG } from '../config/dopplerRadar.config.js';
import {
  clampCoordinatesToBounds,
  clampViewportBounds,
  parsePlaceCoordinates,
  parsePlaceViewportBounds,
  resolvePreferredPlace,
} from '../utils/dopplerLocation.utils.js';

const useDopplerMapLocation = () => {
  const { data: places = [] } = useGetFusionPlacesQuery();
  const { locations } = useLocation();
  const effectiveLocation = locations.effectiveLocation;
  const selectedLocationCoordinates = effectiveLocation?.coords ?? DEFAULT_LOCATION_COORDS;

  const preferredPlace = useMemo(
    () =>
      resolvePreferredPlace({
        places,
        selectedPlaceId: effectiveLocation?.placeId,
      }),
    [effectiveLocation?.placeId, places],
  );

  const mapCoordinates = useMemo(
    () =>
      clampCoordinatesToBounds(
        parsePlaceCoordinates(preferredPlace) ?? selectedLocationCoordinates,
        DOPPLER_CONFIG.maxBounds,
      ),
    [preferredPlace, selectedLocationCoordinates?.[0], selectedLocationCoordinates?.[1]],
  );

  const mapBounds = useMemo(() => {
    if (!DOPPLER_CONFIG.forceHomeZoom) return null;

    const preferredPlaceBounds = clampViewportBounds(
      parsePlaceViewportBounds(preferredPlace),
      DOPPLER_CONFIG.maxBounds,
    );

    return (
      preferredPlaceBounds ??
      clampViewportBounds(effectiveLocation?.bounds, DOPPLER_CONFIG.maxBounds)
    );
  }, [effectiveLocation?.bounds, preferredPlace]);

  const mapCenter = useMemo(
    () => ({ lat: mapCoordinates[0], lng: mapCoordinates[1] }),
    [mapCoordinates],
  );

  return { mapBounds, mapCenter };
};

export default useDopplerMapLocation;
